import { create } from "zustand";
import api from "../service/api";

const getInitialSidebarState = (): boolean => {
  const storedState = localStorage.getItem("sidebarOpen");

  return storedState === "false" ? false : true;
};

// Yeni Tablo Konfigürasyon Yapısı
export interface TableColumnConfig {
  columns: string[];
  widths?: Record<string, number>;
  order?: string[];
}

interface UIState {
  splash: boolean;
  splashDisabled: boolean;
  setSplashDisabled: (val: boolean) => void;
  showSplash: () => void;
  hideSplash: () => void;
  favorites: string[];
  toggleFavorite: (path: string) => void;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  isAccessDenied: boolean;
  deniedModule: string;
  setAccessDenied: (denied: boolean, module?: string) => void;
  activeTab: "all" | "fav";
  setActiveTab: (tab: "all" | "fav") => void;
  collapsedGroups: string[];
  toggleGroup: (groupTitle: string) => void;

  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
  tableColumnSettings: Record<string, TableColumnConfig>;
  setTableColumns: (tableKey: string, config: TableColumnConfig) => void;
  getTableColumns: (tableKey: string) => TableColumnConfig | null;

  cargoHistory: string[];
  addCargoToHistory: (cargo: string) => void;
  removeCargoFromHistory: (cargo: string) => void;

  // Chrome Tarzı Senkronizasyon Durumu İçin
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
  syncMessage: string;
  setSyncStatus: (status: 'idle' | 'syncing' | 'success' | 'error', message?: string) => void;

  // Backend'den gelen ayarları içeri almak için
  hydrateSettings: (settings: any) => void;
  // Arka plan senkronizasyonu için
  syncToBackend: () => void;
}

// Backend'e spam istek atmamak için debounce timer
let syncTimer: ReturnType<typeof setTimeout> | null = null;

export const useUIStore = create<
  UIState & { loading: boolean; setLoading: (val: boolean) => void }
>((set, get) => ({ // 🌟 get fonksiyonunu ekledik
  splash: false,
  splashDisabled: false,
  loading: false,

  syncStatus: 'idle',
  syncMessage: '',
  setSyncStatus: (status, message = '') => set({ syncStatus: status, syncMessage: message }),

  setSplashDisabled: (val: boolean) => set({ splashDisabled: val }),

  showSplash: () => set({ splash: true }),
  hideSplash: () => set({ splash: false }),

  setLoading: (val: boolean) =>
    set((state) => {
      if (val) {
        return { loading: true, splash: !state.splashDisabled };
      } else {
        return { loading: false, splash: false };
      }
    }),

  activeTab:
    (localStorage.getItem("sidebar_active_tab") as "all" | "fav") || "all",

  setActiveTab: (tab) => {
    localStorage.setItem("sidebar_active_tab", tab);
    set({ activeTab: tab });
    get().syncToBackend();
  },

  collapsedGroups: (() => {
    try {
      return JSON.parse(localStorage.getItem("sidebar_collapsed_groups") || "[]");
    } catch {
      return [];
    }
  })(),

  toggleGroup: (groupTitle: string) => {
    const state = get();
    const isCollapsed = state.collapsedGroups.includes(groupTitle);
    const newCollapsed = isCollapsed
      ? state.collapsedGroups.filter((t) => t !== groupTitle)
      : [...state.collapsedGroups, groupTitle];

    localStorage.setItem("sidebar_collapsed_groups", JSON.stringify(newCollapsed));
    set({ collapsedGroups: newCollapsed });
    get().syncToBackend();
  },

  favorites: (() => {
    try {
      return JSON.parse(localStorage.getItem("menu_favorites") || "[]");
    } catch {
      return [];
    }
  })(),

  cargoHistory: (() => {
    try {
      return JSON.parse(localStorage.getItem("cargo_history") || "[]");
    } catch {
      return [];
    }
  })(),

  addCargoToHistory: (cargo: string) => {
    if (!cargo || cargo === "-") return;
    const state = get();
    // Kendisini çıkar ve en başa ekle, max 20 kargo tut
    const updated = [cargo, ...state.cargoHistory.filter(c => c !== cargo)].slice(0, 20);
    localStorage.setItem("cargo_history", JSON.stringify(updated));
    set({ cargoHistory: updated });
    get().syncToBackend();
  },

  removeCargoFromHistory: (cargo: string) => {
    const state = get();
    const updated = state.cargoHistory.filter(c => c !== cargo);
    localStorage.setItem("cargo_history", JSON.stringify(updated));
    set({ cargoHistory: updated });
    get().syncToBackend();
  },

  viewMode: (localStorage.getItem("employee_report_view_mode") as 'grid' | 'list') || 'grid',

  setViewMode: (mode) => {
    localStorage.setItem("employee_report_view_mode", mode);
    set({ viewMode: mode });
    get().syncToBackend();
  },

  toggleFavorite: (path: string) => {
    const state = get();
    const isFav = state.favorites.includes(path);
    const newFavs = isFav
      ? state.favorites.filter((p) => p !== path)
      : [...state.favorites, path];

    localStorage.setItem("menu_favorites", JSON.stringify(newFavs));
    set({ favorites: newFavs });
    get().syncToBackend();
  },

  sidebarOpen: getInitialSidebarState(),

  toggleSidebar: () => {
    const state = get();
    const newState = !state.sidebarOpen;
    localStorage.setItem("sidebarOpen", newState.toString());
    set({ sidebarOpen: newState });
    // sidebarOpen apiye gönderilmek istenmediği için syncToBackend() çağrılmadı
  },

  isAccessDenied: false,
  deniedModule: "",
  setAccessDenied: (denied, module = "") =>
    set({ isAccessDenied: denied, deniedModule: module }),

  // 🌟 YENİ: Tablo Kolon Yönetimi Fonksiyonları
  tableColumnSettings: (() => {
    try {
      const settings: Record<string, any> = {};
      if (typeof window !== "undefined" && window.localStorage) {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('table_cols_')) {
            const tableKey = key.replace('table_cols_', '');
            try {
              settings[tableKey] = JSON.parse(localStorage.getItem(key) || '{}');
            } catch (e) { }
          }
        }
      }
      return settings;
    } catch {
      return {};
    }
  })(),

  setTableColumns: (tableKey, config) => {
    localStorage.setItem(`table_cols_${tableKey}`, JSON.stringify(config));
    const currentState = get();
    set({
      tableColumnSettings: { ...currentState.tableColumnSettings, [tableKey]: config },
    });
    get().syncToBackend();
  },

  getTableColumns: (tableKey) => {
    // Önce memory'deki state'e bak, yoksa localStorage'dan oku
    const cached = get().tableColumnSettings[tableKey];
    if (cached) return cached;

    try {
      const stored = localStorage.getItem(`table_cols_${tableKey}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Memory state'i de eşitleyelim
        set((state) => ({
          tableColumnSettings: { ...state.tableColumnSettings, [tableKey]: parsed },
        }));
        return parsed;
      }
    } catch {
      return null;
    }
    return null;
  },

  // --- YENİ: Backend Entegrasyonu Fonksiyonları ---

  // Giriş yapıldığında veya sayfa yenilendiğinde (/auth/me) backend'den gelen veriyi içeri aktarır
  hydrateSettings: (settings) => {
    if (!settings) return;

    set((state) => {
      const newState = { ...state };

      if (settings.activeTab !== undefined) {
        newState.activeTab = settings.activeTab;
        localStorage.setItem("sidebar_active_tab", settings.activeTab);
      }
      if (settings.favorites && Array.isArray(settings.favorites)) {
        newState.favorites = settings.favorites;
        localStorage.setItem("menu_favorites", JSON.stringify(settings.favorites));
      }
      if (settings.collapsedGroups && Array.isArray(settings.collapsedGroups)) {
        newState.collapsedGroups = settings.collapsedGroups;
        localStorage.setItem("sidebar_collapsed_groups", JSON.stringify(settings.collapsedGroups));
      }
      if (settings.viewMode !== undefined) {
        newState.viewMode = settings.viewMode;
        localStorage.setItem("employee_report_view_mode", settings.viewMode);
      }
      if (settings.cargoHistory && Array.isArray(settings.cargoHistory)) {
        newState.cargoHistory = settings.cargoHistory;
        localStorage.setItem("cargo_history", JSON.stringify(settings.cargoHistory));
      }

      // 🌟 YENİ: JSON Güvenlik Kontrolü
      if (settings.tableColumnSettings) {
        let incomingTableSettings = settings.tableColumnSettings;

        // Prisma/NestJS kaynaklı string kalma ihtimaline karşı parse et
        if (typeof incomingTableSettings === 'string') {
          try {
            incomingTableSettings = JSON.parse(incomingTableSettings);
          } catch (e) {
            console.error("Tablo ayarları parse edilemedi:", e);
          }
        }

        if (incomingTableSettings && typeof incomingTableSettings === 'object') {
          // Backend'den gelen ayarları mevcut olanlarla birleştir (backend öncelikli)
          newState.tableColumnSettings = { ...newState.tableColumnSettings, ...incomingTableSettings };
          Object.keys(incomingTableSettings).forEach(key => {
            localStorage.setItem(`table_cols_${key}`, JSON.stringify(incomingTableSettings[key]));
          });
        }
      }

      return newState;
    });
  },

  // Debounce (Gecikmeli) Sessiz Senkronizasyon
  syncToBackend: () => {
    // Eğer önceki bir zamanlayıcı varsa iptal et
    if (syncTimer) clearTimeout(syncTimer);

    // Son işlemden 2 saniye sonra API'ye gönder (Kullanıcı sürekli bir şeylere tıklıyorsa bekle)
    syncTimer = setTimeout(async () => {
      const state = get();
      const payload = {
        activeTab: state.activeTab,
        favorites: state.favorites,
        collapsedGroups: state.collapsedGroups,
        viewMode: state.viewMode,
        tableColumnSettings: state.tableColumnSettings,
        cargoHistory: state.cargoHistory
      };

      // Sağ altta Chrome stili sabit durum yazısını başlatıyoruz
      get().setSyncStatus('syncing', 'Ayarlar kaydediliyor...');

      // İşlem bitene kadar global Splash (tam ekran yükleniyor) animasyonunu kilitliyoruz
      get().setSplashDisabled(true);

      try {
        // Eğer api servisinizde (axios interceptor) global loading varsa bunu engellemek için hide-loading header'ı ekliyoruz.
        await api.put("/user/ui-settings/my", { uiSettings: payload }, {
          headers: { "hide-loading": "true", "x-silent": "true" }
        });

        // İşlem başarılı olunca mesajı güncelliyoruz
        get().setSyncStatus('success', 'Ayarlar eşitlendi.');
        setTimeout(() => get().setSyncStatus('idle'), 2000); // 2 sn sonra gizle
      } catch (error) {
        console.error("UI ayarları backend'e kaydedilemedi:", error);
        get().setSyncStatus('error', 'Kayıt başarısız oldu!');
        setTimeout(() => get().setSyncStatus('idle'), 3000);
      } finally {
        // İşlem bittiğinde Splash kilidini tekrar kaldırıyoruz
        get().setSplashDisabled(false);
      }
    }, 2000);
  }
}));

